import { Stack } from '@mui/material';

import type { SelectedWelcomeBonus, WelcomeBonusCampaign, WelcomeBonusCampaignBonus } from '../../../../@types/api';
import { AFFILIATE_CAMPAIGN_PRODUCT } from '../../../../constants/enums';
import { useMarketplace } from '../../../../hooks/useMarketplace';
import { formatSnakeCaseString } from '../../../../utils';
import { getCurrencySymbol } from '../../../atoms/BettingInfoDisplay';
import { CasinoReviewFields } from './products/casino';
import { SportsbookReviewFields } from './products/sportsbook';
import { WelcomeBonusFormSection, WelcomeBonusReviewField, WelcomeBonusReviewGrid } from './WelcomeBonusFormPrimitives';

type WelcomeBonusReviewCampaign = WelcomeBonusCampaign | SelectedWelcomeBonus;

const formatAmount = (value: number | null | undefined, currencySymbol: string) =>
  value == null ? null : `${value}${currencySymbol}`;

const getBonusAmountReview = (campaignBonus: WelcomeBonusCampaignBonus | null | undefined, currencySymbol: string) => {
  if (campaignBonus?.bonusPercentage != null) return `${campaignBonus.bonusPercentage}%`;
  if (campaignBonus?.fixedAmount != null) return formatAmount(campaignBonus.fixedAmount, currencySymbol);

  return null;
};

const getWageringReview = (campaignBonus: WelcomeBonusCampaignBonus | null | undefined, currencySymbol: string) => {
  if (campaignBonus?.wageringRequirement != null) {
    return {
      type: 'Fixed amount',
      value: formatAmount(campaignBonus.wageringRequirement, currencySymbol),
    };
  }

  if (campaignBonus?.multiplier != null) {
    return {
      type: 'Multiplier',
      value: `${campaignBonus.multiplier}x`,
    };
  }

  return { type: null, value: null };
};

export const WelcomeBonusEligibilityReview = ({
  campaign,
  showDescription = false,
  showScope = true,
}: {
  campaign: WelcomeBonusReviewCampaign;
  showDescription?: boolean;
  showScope?: boolean;
}) => (
  <WelcomeBonusFormSection title='Eligibility'>
    <WelcomeBonusReviewGrid>
      <WelcomeBonusReviewField label='Product' value={formatSnakeCaseString(campaign.product)} />
      {showScope && (
        <WelcomeBonusReviewField
          label='Scope'
          value={campaign.welcomeBonusScope ? formatSnakeCaseString(campaign.welcomeBonusScope) : null}
        />
      )}
      <WelcomeBonusReviewField
        label='Eligibility criteria'
        value={
          campaign.campaignBonus?.eligibilityCriteria
            ? formatSnakeCaseString(campaign.campaignBonus.eligibilityCriteria)
            : null
        }
      />
      <WelcomeBonusReviewField
        label='Validity period'
        value={campaign.validityPeriod ? formatSnakeCaseString(campaign.validityPeriod) : null}
      />
      {'affiliate' in campaign && campaign.affiliate && (
        <WelcomeBonusReviewField label='Affiliate' value={campaign.affiliate.userName ?? campaign.affiliate.id} />
      )}
      {showDescription && <WelcomeBonusReviewField label='Bonus Description' value={campaign.description ?? '--'} />}
    </WelcomeBonusReviewGrid>
  </WelcomeBonusFormSection>
);

const WelcomeBonusRewardReview = ({
  campaignBonus,
  currencySymbol,
}: {
  campaignBonus?: WelcomeBonusCampaignBonus | null;
  currencySymbol: string;
}) => (
  <WelcomeBonusFormSection title='Reward'>
    <WelcomeBonusReviewGrid>
      <WelcomeBonusReviewField
        label='Bonus type'
        value={campaignBonus?.bonusType ? formatSnakeCaseString(campaignBonus.bonusType) : null}
      />
      <WelcomeBonusReviewField
        label='Awarded Bonus Type'
        value={campaignBonus?.rewardType ? formatSnakeCaseString(campaignBonus.rewardType) : null}
      />
      <WelcomeBonusReviewField label='Bonus amount' value={getBonusAmountReview(campaignBonus, currencySymbol)} />
      {campaignBonus?.bonusPercentage != null && (
        <WelcomeBonusReviewField label='Cap Limit' value={formatAmount(campaignBonus.capLimit, currencySymbol)} />
      )}
      <WelcomeBonusReviewField
        label='Minimum Deposit Amount'
        value={formatAmount(campaignBonus?.minDepositAmount, currencySymbol)}
      />
      <WelcomeBonusReviewField label='Timeframe (days)' value={campaignBonus?.timeframe} />
    </WelcomeBonusReviewGrid>
  </WelcomeBonusFormSection>
);

const WelcomeBonusProductReview = ({
  campaign,
  currencySymbol,
}: {
  campaign: WelcomeBonusReviewCampaign;
  currencySymbol: string;
}) => {
  switch (campaign.product) {
    case AFFILIATE_CAMPAIGN_PRODUCT.SPORTSBOOK:
      return <SportsbookReviewFields campaignBonus={campaign.campaignBonus} />;
    case AFFILIATE_CAMPAIGN_PRODUCT.CASINO:
      return <CasinoReviewFields campaignBonus={campaign.campaignBonus} currencySymbol={currencySymbol} />;
  }
};

const WelcomeBonusWageringReview = ({
  campaignBonus,
  currencySymbol,
}: {
  campaignBonus?: WelcomeBonusCampaignBonus | null;
  currencySymbol: string;
}) => {
  const wagering = getWageringReview(campaignBonus, currencySymbol);

  return (
    <WelcomeBonusFormSection title='Rollover / Wagering'>
      <WelcomeBonusReviewGrid>
        <WelcomeBonusReviewField label='Wagering type' value={wagering.type} />
        <WelcomeBonusReviewField label='Wagering value' value={wagering.value} />
      </WelcomeBonusReviewGrid>
    </WelcomeBonusFormSection>
  );
};

export const WelcomeBonusConfigurationReview = ({ campaign }: { campaign: WelcomeBonusReviewCampaign }) => {
  const { config } = useMarketplace();
  const currencySymbol = getCurrencySymbol(config?.marketplaceConfig?.currency || '') || '';

  return (
    <Stack spacing={2}>
      <WelcomeBonusRewardReview campaignBonus={campaign.campaignBonus} currencySymbol={currencySymbol} />
      <WelcomeBonusProductReview campaign={campaign} currencySymbol={currencySymbol} />
      <WelcomeBonusWageringReview campaignBonus={campaign.campaignBonus} currencySymbol={currencySymbol} />
    </Stack>
  );
};

export const WelcomeBonusReview = ({ campaign }: { campaign: WelcomeBonusReviewCampaign }) => (
  <Stack spacing={2}>
    <WelcomeBonusEligibilityReview campaign={campaign} showDescription />
    <WelcomeBonusConfigurationReview campaign={campaign} />
  </Stack>
);
